import type { PublicProductVariantSummary, SalePriceCents, SalePricing } from '../types/product';

export interface SalePricedProduct {
  readonly price_cents: number;
  readonly sale_price_cents?: number | null;
}

export function hasActiveSale(product: SalePricedProduct): boolean {
  const salePriceCents = product.sale_price_cents;
  return (
    Number.isFinite(product.price_cents) &&
    Number.isFinite(salePriceCents) &&
    salePriceCents !== null &&
    salePriceCents !== undefined &&
    salePriceCents > 0 &&
    salePriceCents < product.price_cents
  );
}

export function getDiscountPercent(product: SalePricedProduct): number | null {
  if (!hasActiveSale(product)) return null;
  const discountRatio =
    (product.price_cents - Number(product.sale_price_cents)) / product.price_cents;
  return Math.round(discountRatio * 100);
}

export function getEffectivePriceCents(product: SalePricedProduct): number {
  return hasActiveSale(product) ? Number(product.sale_price_cents) : product.price_cents;
}

export function getSalePricing(product: SalePricedProduct): SalePricing {
  const isOnSale = hasActiveSale(product);
  return {
    regularPriceCents: product.price_cents,
    salePriceCents: isOnSale ? Number(product.sale_price_cents) : null,
    effectivePriceCents: isOnSale ? Number(product.sale_price_cents) : product.price_cents,
    isOnSale,
    discountPercent: isOnSale ? getDiscountPercent(product) : null,
  };
}

export function getVariantProductPricing(product: {
  readonly summary_variant_id: string | null;
  readonly summary_regular_price_cents: number;
  readonly summary_sale_price_cents: SalePriceCents;
  readonly summary_effective_price_cents: number;
  readonly from_price: boolean;
}): {
  readonly variantId: string | null;
  readonly regularPriceCents: number;
  readonly salePriceCents: SalePriceCents;
  readonly effectivePriceCents: number;
  readonly isOnSale: boolean;
  readonly fromPrice: boolean;
} {
  const isOnSale =
    product.summary_sale_price_cents !== null &&
    product.summary_sale_price_cents > 0 &&
    product.summary_sale_price_cents < product.summary_regular_price_cents;
  return {
    variantId: product.summary_variant_id,
    regularPriceCents: product.summary_regular_price_cents,
    salePriceCents: isOnSale ? product.summary_sale_price_cents : null,
    effectivePriceCents: product.summary_effective_price_cents,
    isOnSale,
    fromPrice: product.from_price,
  };
}

export function getVariantSalePricing(product: {
  readonly sale_summary_variant_id: string | null;
  readonly sale_summary_regular_price_cents: number | null;
  readonly sale_summary_sale_price_cents: SalePriceCents;
  readonly sale_summary_effective_price_cents: number | null;
}): (SalePricing & { readonly variantId: string }) | null {
  if (
    !product.sale_summary_variant_id ||
    product.sale_summary_regular_price_cents === null ||
    product.sale_summary_sale_price_cents === null ||
    product.sale_summary_effective_price_cents === null
  ) {
    return null;
  }

  return {
    variantId: product.sale_summary_variant_id,
    regularPriceCents: product.sale_summary_regular_price_cents,
    salePriceCents: product.sale_summary_sale_price_cents,
    effectivePriceCents: product.sale_summary_effective_price_cents,
    isOnSale: true,
    discountPercent: Math.round(
      ((product.sale_summary_regular_price_cents - product.sale_summary_sale_price_cents) /
        product.sale_summary_regular_price_cents) *
        100,
    ),
  };
}

export interface VariantPriceSummary {
  readonly variantId: string | null;
  readonly regularPriceCents: number;
  readonly salePriceCents: SalePriceCents;
  readonly effectivePriceCents: number;
}

export function getVariantEffectivePriceCents(variant: SalePricedProduct): number {
  return getEffectivePriceCents(variant);
}

function compareVariantSummary(
  a: PublicProductVariantSummary,
  b: PublicProductVariantSummary,
): number {
  const effectiveDelta = getVariantEffectivePriceCents(a) - getVariantEffectivePriceCents(b);
  if (effectiveDelta !== 0) return effectiveDelta;
  const regularDelta = a.price_cents - b.price_cents;
  if (regularDelta !== 0) return regularDelta;
  return a.id.localeCompare(b.id);
}

export function getProductPriceSummary(
  variants: readonly PublicProductVariantSummary[],
): VariantPriceSummary {
  const selected = variants
    .filter((variant) => variant.active)
    .slice()
    .sort(compareVariantSummary)[0];

  if (!selected) {
    return {
      variantId: null,
      regularPriceCents: 0,
      salePriceCents: null,
      effectivePriceCents: 0,
    };
  }

  const salePriceCents = hasActiveSale(selected) ? Number(selected.sale_price_cents) : null;
  return {
    variantId: selected.id,
    regularPriceCents: selected.price_cents,
    salePriceCents,
    effectivePriceCents: salePriceCents ?? selected.price_cents,
  };
}

export function getProductSaleSummary(
  variants: readonly PublicProductVariantSummary[],
): VariantPriceSummary {
  const selected = variants
    .filter((variant) => variant.active && hasActiveSale(variant))
    .slice()
    .sort((a, b) => {
      const saleDelta = Number(a.sale_price_cents) - Number(b.sale_price_cents);
      if (saleDelta !== 0) return saleDelta;
      const regularDelta = a.price_cents - b.price_cents;
      if (regularDelta !== 0) return regularDelta;
      return a.id.localeCompare(b.id);
    })[0];

  if (!selected) {
    return {
      variantId: null,
      regularPriceCents: 0,
      salePriceCents: null,
      effectivePriceCents: 0,
    };
  }

  return {
    variantId: selected.id,
    regularPriceCents: selected.price_cents,
    salePriceCents: Number(selected.sale_price_cents),
    effectivePriceCents: Number(selected.sale_price_cents),
  };
}

export function hasVariantSale(variants: readonly PublicProductVariantSummary[]): boolean {
  return variants.some((variant) => variant.active && hasActiveSale(variant));
}

export function formatVariantOptionSummary(
  parts: readonly { readonly optionName: string; readonly value: string }[],
): string {
  return parts
    .map((part) => `${part.optionName.trim()}: ${part.value.trim()}`)
    .filter((part) => part !== ':')
    .join(' / ')
    .slice(0, 120);
}

export function selectDefaultPublicVariant(
  variants: readonly PublicProductVariantSummary[],
): PublicProductVariantSummary | null {
  return (
    variants.find((variant) => variant.is_default && variant.active) ??
    variants.find((variant) => variant.active) ??
    variants[0] ??
    null
  );
}

export function resolveSelectedPublicVariant(
  variants: readonly PublicProductVariantSummary[],
  selectedOptionValueIds: readonly string[],
): PublicProductVariantSummary | null {
  if (selectedOptionValueIds.length === 0) {
    return selectDefaultPublicVariant(variants);
  }

  const selected = [...selectedOptionValueIds].sort().join('|');
  return (
    variants.find((variant) => [...variant.option_value_ids].sort().join('|') === selected) ?? null
  );
}
